import type { IdVidro } from '../../dominio/didatico/manejoVidros'
import type { PerfilVeiculo } from '../../dominio/didatico/perfilVeiculo'

export interface RegiaoVidro {
  posicao: [number, number, number]
  rotacao: [number, number, number]
  dimensoes: [number, number, number]
}

/**
 * Posição, rotação e tamanho de cada vidro do veículo, derivados do perfil —
 * usado tanto pela oficina de manejo de vidros (um único veículo) quanto
 * pelo Table top (vários veículos), para as duas nunca divergirem se a
 * geometria de um perfil mudar.
 */
export function regioesVidroDoPerfil(perfil: PerfilVeiculo): Record<IdVidro, RegiaoVidro> {
  const meiaLargura = perfil.largura / 2
  const dyColunaA = perfil.tetoY - perfil.cinturaY
  const taxaColunaA = dyColunaA !== 0 ? (perfil.colunaA.zTopo - perfil.colunaA.zBase) / dyColunaA : 0
  const zColunaATopo = perfil.colunaA.zBase + taxaColunaA * (perfil.tetoY - 0.06 - perfil.cinturaY)
  const zTrasDiant = perfil.portaDianteira.zTraseiro + 0.05
  const zFrenteDiant = Math.min(
    perfil.portaDianteira.zDianteiro - 0.05,
    (zColunaATopo + perfil.portaDianteira.zDianteiro) / 2 - 0.02,
  )
  const zDiant = (zFrenteDiant + zTrasDiant) / 2
  const comprimentoDiant = zFrenteDiant - zTrasDiant
  const zTras = (perfil.portaTraseira.zDianteiro + perfil.portaTraseira.zTraseiro) / 2
  const yJanela = (perfil.cinturaY + perfil.tetoY) / 2
  const alturaJanela = perfil.tetoY - perfil.cinturaY - 0.03
  const xLateral = meiaLargura - 0.038
  const tumblehome = 0.065

  // Para-brisa: declive e dimensões reais alinhadas à Coluna A
  const dzA = perfil.colunaA.zTopo - perfil.colunaA.zBase
  const dyA = perfil.tetoY - perfil.cinturaY
  const anguloParabrisa = Math.atan2(dzA, dyA)
  const comprimentoParabrisa = Math.hypot(dzA, dyA)

  // Vigia traseiro: declive e dimensões reais alinhadas à Coluna C
  const dzC = perfil.colunaC.zTopo - perfil.colunaC.zBase
  const dyC = perfil.tetoY - perfil.cinturaY
  const anguloVigia = Math.atan2(dzC, dyC)
  const comprimentoVigia = Math.hypot(dzC, dyC)

  return {
    parabrisa: {
      posicao: [0, yJanela, (perfil.colunaA.zBase + perfil.colunaA.zTopo) / 2],
      rotacao: [anguloParabrisa, 0, 0],
      dimensoes: [perfil.largura - 0.2, comprimentoParabrisa, 0.02],
    },
    portaDianteiraEsquerda: {
      posicao: [-xLateral, yJanela, zDiant],
      rotacao: [Math.PI / 2, Math.PI / 2 - tumblehome, -Math.PI / 2],
      dimensoes: [comprimentoDiant, alturaJanela, 0.018],
    },
    portaDianteiraDireita: {
      posicao: [xLateral, yJanela, zDiant],
      rotacao: [Math.PI / 2, -Math.PI / 2 + tumblehome, Math.PI / 2],
      dimensoes: [comprimentoDiant, alturaJanela, 0.018],
    },
    portaTraseiraEsquerda: {
      posicao: [-xLateral, yJanela, zTras],
      rotacao: [Math.PI / 2, Math.PI / 2 - tumblehome, -Math.PI / 2],
      dimensoes: [perfil.portaTraseira.zDianteiro - perfil.portaTraseira.zTraseiro - 0.08, alturaJanela, 0.018],
    },
    portaTraseiraDireita: {
      posicao: [xLateral, yJanela, zTras],
      rotacao: [Math.PI / 2, -Math.PI / 2 + tumblehome, Math.PI / 2],
      dimensoes: [perfil.portaTraseira.zDianteiro - perfil.portaTraseira.zTraseiro - 0.08, alturaJanela, 0.018],
    },
    vigiaTraseiro: {
      posicao: [0, yJanela, (perfil.colunaC.zBase + perfil.colunaC.zTopo) / 2],
      rotacao: [anguloVigia, 0, 0],
      dimensoes: [perfil.largura - 0.22, comprimentoVigia, 0.02],
    },
  }
}
